import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CompaniesState,
  ICompany,
  ICompanyBranch,
  ICompanyCount,
  ICompanyEmployeeData,
  ICompanyFilesResponseData,
  IUploadCompanyFilesResponseData,
} from "../types/companies";

type CompanyUpdate = Partial<ICompany> & Pick<ICompany, "id">;

const initialState: CompaniesState = {
  companies: [],
  company: null,
  branches: [],
  branchesCompanyId: null,
  branchesLoading: false,
  employees: [],
  employeesCompanyId: null,
  employeesLoading: false,
  creatingEmployeeCompanyId: null,
  files: [],
  filesCompanyId: null,
  filesLimit: 10,
  filesOffset: 0,
  filesTotal: 0,
  filesLoading: false,
  selectedFile: null,
  selectedFileLoading: false,
  updatingFileId: null,
  deletingFileId: null,
  counts: {
    total_company: 0,
    total_inactive_company: 0,
    total_active_company: 0,
  },
  loading: false,
  loadingCompanyId: null,
};

const companies = createSlice({
  name: "companies",
  initialState,
  reducers: {
    setCompanies: (state, action: PayloadAction<ICompany[]>) => {
      state.companies = action.payload;
    },
    setCompany: (state, action: PayloadAction<ICompany | null>) => {
      state.company = action.payload;
    },
    updateCompanyInList: (state, action: PayloadAction<CompanyUpdate>) => {
      state.companies = state.companies.map((company) =>
        company.id === action.payload.id
          ? { ...company, ...action.payload }
          : company,
      );
      if (state.company?.id === action.payload.id) {
        state.company = { ...state.company, ...action.payload };
      }
    },
    removeCompany: (state, action: PayloadAction<number>) => {
      state.companies = state.companies.filter(
        (company) => company.id !== action.payload,
      );
      if (state.company?.id === action.payload) state.company = null;
    },
    setCompanyLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setLoadingCompanyId: (state, action: PayloadAction<number | null>) => {
      state.loadingCompanyId = action.payload;
    },

    setTotalCompany: (state, action: PayloadAction<ICompanyCount["data"]>) => {
      state.counts = action.payload;
    },

    loadBranches: (state, action: PayloadAction<number>) => {
      state.branchesCompanyId = action.payload;
      state.branches = [];
      state.branchesLoading = true;
    },
    setBranches: (
      state,
      action: PayloadAction<{ companyId: number; branches: ICompanyBranch[] }>,
    ) => {
      if (state.branchesCompanyId !== action.payload.companyId) return;
      state.branches = action.payload.branches;
      state.branchesLoading = false;
    },
    finishLoadingBranches: (state, action: PayloadAction<number>) => {
      if (state.branchesCompanyId === action.payload) {
        state.branchesLoading = false;
      }
    },
    loadEmployees: (state, action: PayloadAction<number>) => {
      state.employeesCompanyId = action.payload;
      state.employees = [];
      state.employeesLoading = true;
    },
    setEmployees: (
      state,
      action: PayloadAction<{
        companyId: number;
        employees: ICompanyEmployeeData;
      }>,
    ) => {
      if (state.employeesCompanyId !== action.payload.companyId) return;
      state.employees = action.payload.employees.items;
      state.employeesLoading = false;
    },
    finishLoadingEmployees: (state, action: PayloadAction<number>) => {
      if (state.employeesCompanyId === action.payload) {
        state.employeesLoading = false;
      }
    },
    setCreatingEmployeeCompanyId: (
      state,
      action: PayloadAction<number | null>,
    ) => {
      state.creatingEmployeeCompanyId = action.payload;
    },

    loadFiles: (
      state,
      action: PayloadAction<{
        companyId: number;
        limit: number;
        offset: number;
      }>,
    ) => {
      state.filesCompanyId = action.payload.companyId;
      state.filesLimit = action.payload.limit;
      state.filesOffset = action.payload.offset;
      state.filesLoading = true;
    },
    setFiles: (
      state,
      action: PayloadAction<{
        companyId: number;
        page: ICompanyFilesResponseData;
      }>,
    ) => {
      if (state.filesCompanyId !== action.payload.companyId) return;
      state.files = action.payload.page.data;
      state.filesLimit = action.payload.page.limit;
      state.filesOffset = action.payload.page.offset;
      state.filesTotal = action.payload.page.total;
      state.filesLoading = false;
    },
    finishLoadingFiles: (state, action: PayloadAction<number>) => {
      if (state.filesCompanyId === action.payload) state.filesLoading = false;
    },
    setSelectedFile: (
      state,
      action: PayloadAction<IUploadCompanyFilesResponseData | null>,
    ) => {
      state.selectedFile = action.payload;
      state.selectedFileLoading = false;
    },
    setSelectedFileLoading: (state, action: PayloadAction<boolean>) => {
      state.selectedFileLoading = action.payload;
    },
    updateFile: (
      state,
      action: PayloadAction<{
        companyId: number;
        file: IUploadCompanyFilesResponseData;
      }>,
    ) => {
      if (state.filesCompanyId !== action.payload.companyId) return;
      state.files = state.files.map((file) =>
        file.id === action.payload.file.id ? action.payload.file : file,
      );
      state.selectedFile = action.payload.file;
      state.updatingFileId = null;
    },
    setUpdatingFileId: (state, action: PayloadAction<number | null>) => {
      state.updatingFileId = action.payload;
    },
    removeFile: (
      state,
      action: PayloadAction<{ companyId: number; fileId: number }>,
    ) => {
      if (state.filesCompanyId === action.payload.companyId) {
        const exists = state.files.some(
          (file) => file.id === action.payload.fileId,
        );
        state.files = state.files.filter(
          (file) => file.id !== action.payload.fileId,
        );
        if (exists) state.filesTotal = Math.max(0, state.filesTotal - 1);
      }
      if (state.selectedFile?.id === action.payload.fileId) {
        state.selectedFile = null;
      }
      state.deletingFileId = null;
    },
    setDeletingFileId: (state, action: PayloadAction<number | null>) => {
      state.deletingFileId = action.payload;
    },
  },
});

export const {
  setCompanies,
  setCompany,
  setTotalCompany,
  updateCompanyInList,
  removeCompany,
  setCompanyLoading,
  setLoadingCompanyId,
  loadBranches,
  setBranches,
  finishLoadingBranches,
  loadEmployees,
  setEmployees,
  finishLoadingEmployees,
  setCreatingEmployeeCompanyId,
  setFiles,
  loadFiles,
  finishLoadingFiles,
  setSelectedFile,
  setSelectedFileLoading,
  updateFile,
  setUpdatingFileId,
  removeFile,
  setDeletingFileId,
} = companies.actions;

export default companies.reducer;
